import {
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback } from 'react';

import { ratingApi } from 'apis/rating';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import styles from './surveyDeleteModal.module.scss';

export type FieldValues = {
  surveyType: Satisfaction.SurveyType;
  surveyName: string;
};

const emptyArray: Satisfaction.RatingSettingsLink[] = [];

/** A component that displays delete survey modal. */
export const SurveyDeleteModal = createAppModal(
  { type: 'settings:deleteSurvey' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const ratingSettingsId = modal.arg.id;
    const readSurveys = modal.arg.readSurveys;

    const notify = useNotifications();

    const { isLoading, data } = ratingApi.useReadSurveyConnectionsQuery(
      {
        ratingSettingId: ratingSettingsId,
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            data: data?.data?.rating_settings_links ?? emptyArray,
            ...rest,
          };
        },
      },
    );

    const [deleteRatingSettings, { isFetching }] =
      ratingApi.useLazyDeleteRatingSettingsQuery();

    const handleClose = () => {
      hide?.();
    };

    const onDelete = async () => {
      try {
        await deleteRatingSettings({
          ratingSettingsId: ratingSettingsId,
        }).unwrap();

        readSurveys();
        handleClose();

        notify(
          {
            title: 'Success',
            text: 'The survey has been deleted.',
            icon: 'done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch {
        notify(
          {
            title: 'Error while deleting survey.',
          },
          NotificationType.Error,
        );
      }
    };

    const getBodyMessage = useCallback(
      (data: readonly Satisfaction.RatingSettingsLink[]) => {
        if (data?.length) {
          return (
            <>
              <Text.Paragraph withMarginBottom>
                You are about to turn off the CSAT survey and delete its
                language customization. The following phone numbers are
                associated with the survey:
              </Text.Paragraph>
              <ul>
                {data?.map((settings, index) => (
                  <li key={index}>{settings.contact_point}</li>
                ))}
              </ul>
            </>
          );
        }

        return (
          <Text.Paragraph withMarginBottom>
            Are you sure you want to permanently delete the survey? This action
            cannot be undone.
          </Text.Paragraph>
        );
      },
      [],
    );

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead
          title={
            <FlexBox gap="200">
              <Text.Heading>Delete survey?</Text.Heading>
            </FlexBox>
          }
          onClose={handleClose}
        />

        <ModalBody>
          {isLoading ? (
            <FlexBox justifyContent="center" alignItems="center" height="100px">
              <Spinner size={32} />
            </FlexBox>
          ) : (
            getBodyMessage(data)
          )}
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName="Delete"
          confirmOnClick={() => onDelete()}
          confirmProps={{
            disabled: isFetching || isLoading,
            variant: 'danger',
          }}
        />
      </Modal>
    );
  },
);
